import { FC, useEffect, useRef } from "react";
import {
  T_GenericSSORedirectPayload,
  T_HISSORedirectPayload,
  TriggerGenerateSSO_OLD,
} from "../Sso_Types";
import {
  useLazyGenerateSso_OldQuery,
  useLazyRedirectUrlApiQuery,
} from "../Sso_Api";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../redux-store/CustomReduxHooks";
import { updateLoadingState } from "../../Feature_Loading/Loading_Slice";
import { API_STATUS_CODE } from "../../../utils/ApiEndpoints";
import { setError } from "../../../redux-store/reducers/CsErrorSlice";
import { useNavigate } from "react-router-dom";
import {
  clearSsoSlice,
  clearSsoSliceWithTriggerId,
  disableShowLoading,
  updateSSOSlice,
} from "../Sso_Slice";
import {
  HiDestinationLobId,
  LiDestinationLobId,
  liRedirectionFunctionalityCodes,
  LoansDestinationLobId,
  OneLoginLobCode,
} from "../../../utils/constants";
import { nanoid } from "@reduxjs/toolkit";
import { T_ApiResponse } from "../../../globalTypes/GlobalTypes";
import { isEmpty, uniqueId } from "lodash";
import { windowOpen } from "../../../utils/helper";
import { R_LiRoutes } from "../../../utils/Routes";
import CsSessionStorageUtils from "../../../utils/CsSessionStorageUtils";

const GenerateSsoOld: FC = () => {
  const ssoSlice = useAppSelector(
    (state) => state.SsoSlice.ssoData as TriggerGenerateSSO_OLD
  );
  const triggerIdFromSSO = useAppSelector((st) => st.SsoSlice.triggerId);
  const csAppSlice = useAppSelector((state) => state.CsAppSlice);
  const triggerId = useRef<string>();
  const redirectionUrlFromLobApi = useRef<string>();
  const [redirectionApiCall] = useLazyRedirectUrlApiQuery();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [
    callGenerateSSOOld,
    { data: generateSsoOldData, isFetching, isUninitialized },
  ] = useLazyGenerateSso_OldQuery();
  const resetRedirectionVars = () => {
    redirectionUrlFromLobApi.current = "";
    triggerId.current = "";
  };
  useEffect(() => {
    if (!isEmpty(ssoSlice.appVersion)) {
      const reqPayload = { ...ssoSlice };
      reqPayload.udp1 = csAppSlice.traceId;
      if (isEmpty(reqPayload.udp2)) {
        reqPayload.udp2 =
          reqPayload?.destinationLobId === "03" ? reqPayload.panNumber : "";
      }
      reqPayload.udp3 = reqPayload.mobileNo;
      reqPayload.udp4 = "REQ" + nanoid(9);
      resetRedirectionVars();
      dispatch(updateSSOSlice({ ...ssoSlice, appVersion: "" }));
      callGenerateSSOOld(reqPayload);
    }
  }, [ssoSlice.triggerGenerateSSO_Old]);
  useEffect(() => {
    if (isFetching || isUninitialized) {
      return;
    }
    if (
      generateSsoOldData?.code === API_STATUS_CODE.SUCCESS &&
      generateSsoOldData.payload?.ReturnCode === "1"
    ) {
      const redirectionUrl = ssoSlice.redirectionUrl;
      const genericRequestData: T_GenericSSORedirectPayload = {
        TokenNo: generateSsoOldData.payload.TokenNo,
        TokenRefNo: generateSsoOldData.payload.TokenRefNo,
      };
      let headers: any = {
        "Content-Type": "application/x-www-form-urlencoded",
      };
      let requestData;
      if (ssoSlice.destinationLobId === LoansDestinationLobId) {
        handleRedirectionResult({
          data: {
            headers: {
              responseUrl: `${redirectionUrl}?Token=${generateSsoOldData.payload.TokenNo}&TokenRef=${generateSsoOldData.payload.TokenRefNo}`,
            },
          },
        });
        return;
      }
      if (ssoSlice.destinationLobId === HiDestinationLobId) {
        // Add additional parameters if the condition is met
        genericRequestData.UserId = ssoSlice?.userId; // Replace ssoSlice.userId with the actual source of userId
        genericRequestData.ApiPassword = ssoSlice.apiPassword; // Replace ssoSlice.apiPassword
        headers = { "Content-Type": "application/json" };
        const hiRequestData: T_HISSORedirectPayload = {
          ValidateTokenRequest: {
            ...genericRequestData,
          },
        };
        requestData = { ...hiRequestData };
      } else {
        if (ssoSlice.destinationLobId === "03") {
          headers["auth-token"] =
            csAppSlice.tokenDetails?.external.access_token;
        }
        requestData = { ...genericRequestData };
      }
      if (!isEmpty(redirectionUrl)) {
        CsSessionStorageUtils.clear("CAMPAIGN_IDENTIFIERS");
      }
      redirectionApiCall({
        url: redirectionUrl,
        type: "POST",
        headers: headers,
        data: requestData,
      }).then((r: any) => {
        handleRedirectionResult(r);
      });
    } else {
      dispatch(disableShowLoading());
      dispatch(clearSsoSlice());
      resetRedirectionVars();
      if (
        liRedirectionFunctionalityCodes.includes(ssoSlice.functionality) &&
        ssoSlice.destinationLobId === LiDestinationLobId
      ) {
        navigate(R_LiRoutes.li_UrlNotFound);
      } else {
        const err: Error = new Error("Generate SSO failed");
        dispatch(
          setError({
            error: err,
            message: `${"" + generateSsoOldData?.rootCause}`,
          })
        );
      }
    }
  }, [isFetching]);

  const handleRedirectionResult = (r: any) => {
    dispatch(updateLoadingState(false));
    console.log("response: ", r);
    if (!r.error && r.data) {
      if ("" + ssoSlice.destinationLobId != HiDestinationLobId) {
        console.log("headers: ", r.data?.headers.responseUrl);
        redirectionUrlFromLobApi.current = r.data?.headers?.responseUrl;
      } else if (ssoSlice.destinationLobId === HiDestinationLobId) {
        redirectionUrlFromLobApi.current = r?.data?.payload;
      } else if (ssoSlice.redirectionUrl == OneLoginLobCode) {
        redirectionUrlFromLobApi.current = ssoSlice.redirectionUrl;
      }
      triggerId.current = uniqueId();
      dispatch(clearSsoSliceWithTriggerId(triggerId.current));
    } else if (r.error) {
      dispatch(clearSsoSlice());
      resetRedirectionVars();
      if (
        liRedirectionFunctionalityCodes.includes(ssoSlice.functionality) &&
        ssoSlice.destinationLobId === LiDestinationLobId
      ) {
        navigate(R_LiRoutes.li_UrlNotFound);
      } else {
        const err: Error = new Error("Generate SSO failed");
        dispatch(
          setError({
            error: r.error as T_ApiResponse<any>,
            message: err.message,
          })
        );
      }
    }
  };

  useEffect(() => {
    if (
      redirectionUrlFromLobApi.current &&
      triggerId.current === triggerIdFromSSO
    ) {
      windowOpen(redirectionUrlFromLobApi.current, "_self");
    }
  }, [triggerIdFromSSO]);

  return <></>;
};
export default GenerateSsoOld;
